import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiTrash2 } = FiIcons;

const QuestionField = ({ question, value, onChange }) => {
  const baseClasses = "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all";
  
  const isCustomValue = value && question.type === 'select' && !question.options?.filter(o => o !== 'Other').includes(value);
  const [showOtherInput, setShowOtherInput] = useState(isCustomValue);

  useEffect(() => {
    if (isCustomValue) setShowOtherInput(true);
  }, [value, isCustomValue]);

  const handleSelectChange = (val) => {
    if (val === 'Other') {
      setShowOtherInput(true);
      onChange('');
    } else {
      setShowOtherInput(false);
      onChange(val);
    }
  };

  const addListItem = () => {
    const current = Array.isArray(value) ? value : [];
    onChange([...current, { q: '', a: '' }]);
  };

  const updateListItem = (index, field, val) => {
    const current = [...value];
    current[index][field] = val;
    onChange(current);
  };

  const removeListItem = (index) => {
    const current = value.filter((_, i) => i !== index);
    onChange(current);
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {question.label} {question.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      {question.type === 'text' && (
        <input 
          type="text" 
          value={value || ''} 
          onChange={(e) => onChange(e.target.value)} 
          className={baseClasses} 
          placeholder={question.placeholder || "Enter details..."} 
        />
      )}
      
      {question.type === 'textarea' && (
        <textarea 
          rows={3} 
          value={value || ''} 
          onChange={(e) => onChange(e.target.value)} 
          className={baseClasses} 
          placeholder={question.placeholder || "Describe in detail..."} 
        />
      )}
      
      {question.type === 'select' && (
        <div className="space-y-3">
          <select 
            value={showOtherInput ? 'Other' : (value || '')} 
            onChange={(e) => handleSelectChange(e.target.value)} 
            className={baseClasses}
          >
            <option value="">Select an option</option>
            {question.options?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          <AnimatePresence>
            {showOtherInput && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <input 
                  type="text" 
                  value={value || ''} 
                  onChange={(e) => onChange(e.target.value)} 
                  className={`${baseClasses} border-blue-300 bg-blue-50/30`} 
                  placeholder="Please specify..." 
                  autoFocus 
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
      
      {question.type === 'checkbox' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {question.options?.map(opt => (
            <label key={opt} className="flex items-center p-2 rounded-lg hover:bg-gray-50 cursor-pointer border border-transparent hover:border-gray-200 transition-all">
              <input 
                type="checkbox" 
                checked={value?.includes(opt) || false} 
                onChange={(e) => {
                  const current = value || [];
                  onChange(e.target.checked ? [...current, opt] : current.filter(v => v !== opt));
                }} 
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" 
              />
              <span className="ml-2 text-sm text-gray-600">{opt}</span>
            </label>
          ))}
        </div>
      )}

      {question.type === 'color-palette' && (
        <div className="flex flex-col gap-3">
          <div className="flex gap-2">
            <input 
              type="text" 
              value={value || ''} 
              onChange={(e) => onChange(e.target.value)} 
              placeholder="e.g. #1A73E8, Ivory, Midnight Blue" 
              className={baseClasses} 
            />
            <input 
              type="color" 
              onChange={(e) => {
                const current = value || '';
                onChange(current ? `${current}, ${e.target.value}` : e.target.value);
              }} 
              className="h-10 w-12 rounded-lg cursor-pointer border border-gray-300 p-1" 
            />
          </div>
          <p className="text-xs text-gray-500">Add multiple colors by typing or using the picker.</p>
        </div>
      )}

      {question.type === 'dynamic-list' && (
        <div className="space-y-4">
          <div className="space-y-3">
            {(value || []).map((item, idx) => (
              <motion.div 
                key={idx} 
                initial={{ opacity: 0, x: -10 }} 
                animate={{ opacity: 1, x: 0 }} 
                className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3 relative group"
              >
                <button 
                  onClick={() => removeListItem(idx)}
                  className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                >
                  <SafeIcon icon={FiTrash2} size={14} />
                </button>
                <div className="pr-8">
                  <input 
                    type="text" 
                    value={item.q} 
                    onChange={(e) => updateListItem(idx, 'q', e.target.value)}
                    placeholder="Question / Topic"
                    className={`${baseClasses} bg-white text-sm font-semibold mb-2`}
                  />
                  <textarea 
                    rows={2} 
                    value={item.a} 
                    onChange={(e) => updateListItem(idx, 'a', e.target.value)}
                    placeholder="Your answer or additional context..."
                    className={`${baseClasses} bg-white text-sm`}
                  />
                </div>
              </motion.div>
            ))}
          </div>
          <button 
            onClick={addListItem}
            className="w-full py-2 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50 transition-all flex items-center justify-center gap-2 text-sm font-medium"
          >
            <SafeIcon icon={FiPlus} /> Add Custom Question
          </button>
        </div>
      )}
    </div>
  );
};

export default QuestionField;